import { makeStyles, Theme, createStyles, Container, Grid, Paper, Typography, TableCell } from "@material-ui/core";
import { Fragment, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import Editor from "../../../Editor";
import { ISortableTableHeader, TableHeader } from "../../../../../stores/models/TableHeader";
import FormLoadingSpinner from "../../../../Organization/VolunteerOpportunities/FormLoadingSpinner";
import { Option } from "../../../../../stores/models/Option";
import RecordEditorDialog from "../RecordEditorDialog";
import ConfirmationDialog from "../../../../Shared/ConfirmationDialog";
import { getInitialDialogStates } from "../../../../../logic/DialogStateDictionary";
import RecordTable from "../../../../Shared/Table/NewRecordTable";
import { TableController } from "../../../../../stores/models/TableController";
import AdministratorsOverviewMainMenu from "./AdministratorsOverviewMainMenu";
import AdministratorsOverviewSelectionMenu from "./AdministratorsOverviewSelectionMenu";
import AddAdminForm from "./AddAdminForm";
import { IAdministratorOverview } from "../../../../../stores/models/interfaces/IAdministratorOverview";
import { IOrganizationAdminInvite, OrganizationAdminInvite } from "../../../../../stores/models/OrganizationAdminInvite";
import { AppError } from "../../../../../stores/models/errors/AppError";
import { Alert, AlertTitle } from "@material-ui/lab";
import { useRootStore } from "../../../../../shared/general/hooks";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paper: {
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            whiteSpace: 'pre-line'
        },
        selectionToolbar: {
            [theme.breakpoints.down('sm')]: {
                marginTop: theme.spacing(-1)
            },
            marginTop: theme.spacing(-1.5)
        },
        alert: {
            marginBottom: theme.spacing(2),
        }
    }),
);

const headCells = ([
    { id: 'checkbox', alignment: 'center', disablePadding: true, sortable: false, checkbox: true },
    { id: 'email', alignment: 'left', disablePadding: false, label: 'Email', sortable: true, sortValue: 'email' },
    { id: 'status', alignment: 'left', disablePadding: false, label: 'Status', sortable: true, sortValue: 'status' },
] as ISortableTableHeader<IAdministratorOverview>[]);

const dataCell = (key: keyof IAdministratorOverview) => {
    return (option: Option<IAdministratorOverview>, rowIndex: number, cellIndex: number) => {
        return (
            <TableCell align="left" key={`${rowIndex}-${cellIndex}`}>{option.object[key]}</TableCell>
        );
    }
};

enum DialogType {
    AddNew = "add",
    DeletionConfirmation = 'delete',
}

interface AdministratorsOverviewProps {
    tableController: TableController<'email', IAdministratorOverview, IOrganizationAdminInvite>;
}

const AdministratorsOverview = observer((props: AdministratorsOverviewProps) => {

    const classes = useStyles();
    const rootStore = useRootStore();
    const loadedRecords = props.tableController.records;

    /*** State ***/

    const [dialogStates] = useState(getInitialDialogStates(Object.values(DialogType)));
    const [newRecord] = useState(new OrganizationAdminInvite());
    const [clientValidationError, setClientValidationError] = useState(false);
    const [addRecordError, setAddRecordError] = useState<AppError>();

    const tableHeaderCells = useMemo(() => headCells.map(headCell => new TableHeader(headCell)), []);

    /*** Effects ***/

    useEffect(() => {
        if (dialogStates[DialogType.AddNew].open) {
            setClientValidationError(false);
            setAddRecordError(undefined);
        }
    }, [dialogStates[DialogType.AddNew].open]);

    /********* Event handler *********/

    const onClearSelections = () => {
        props.tableController.tableState.setUnloadedSelected(false);
        props.tableController.records.setSelections([]);
    }

    const onAdminRowClicked = (admin: IAdministratorOverview) => {
        props.tableController.records.toggleSelection(admin);
    }

    const onConfirmNewAdmin = async (invite: OrganizationAdminInvite) => {
        invite.setAllFieldsDirty();
        if (invite.validated) {
            setClientValidationError(false);
            const addNewDialog = dialogStates[DialogType.AddNew];
            addNewDialog.setLoading(true);
            const response = await props.tableController.add(invite);

            if (response) {
                addNewDialog.setLoading(false);
                if (response instanceof AppError) {
                    setAddRecordError(response);
                } else {
                    addNewDialog.setOpen(false);
                }
            }
        } else {
            setClientValidationError(true);
        }
    }

    const onDeleteClicked = () => {
        dialogStates.delete.setOpen(true);
    }

    const onDeletionConfirmed = async () => {
        dialogStates.delete.setLoading(true);
        const response = await props.tableController.deleteSelectedRecords();
        dialogStates.delete.setLoading(false);
        if (response?.success) {
            dialogStates.delete.setOpen(false);
        }
    }

    /********* Helper methods *********/

    const isDeletingSelf = () => {
        const currentEmail = rootStore.userStore.user.email;
        const emails = props.tableController.records.selectedOptions.map(option => option.email);
        const deletingSelf = emails.findIndex(email => email === currentEmail) !== -1;
        return deletingSelf;
    }

    const isDeletingAllRemainingAdmins = () => {
        // TODO: This client validation could be incorrect if there are multiple pages of administrators
        const numActiveAdmins = props.tableController.records.optionObjects.filter(record => record.status === 'Active').length;
        const numSelectedActiveAdmins = props.tableController.records.selectedOptions.filter(record => record.status === 'Active').length;
        return numSelectedActiveAdmins === numActiveAdmins;
    }

    /********* Render *********/

    return (
        <Editor editsMade={false}>
            {(editorState) => (
                <Container>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Paper className={classes.paper}>
                                <Container>
                                    <Typography variant="h4">
                                        Administrators
                                    </Typography>
                                    {/* Toolbar Buttons */}
                                    <AdministratorsOverviewMainMenu
                                        onNewRecordClicked={() => dialogStates[DialogType.AddNew].setOpen(true)}
                                    />
                                    {props.tableController.numSelectedIncludingUnloaded > 0 &&
                                        <AdministratorsOverviewSelectionMenu
                                            numSelections={props.tableController.numSelectedIncludingUnloaded}
                                            onDeleteClicked={onDeleteClicked}
                                            onClearSelections={onClearSelections}
                                            className={classes.selectionToolbar}
                                        />
                                    }
                                    {/* Table */}
                                    <RecordTable
                                        controller={props.tableController}
                                        tableHeaderCells={tableHeaderCells}
                                        displayFunctions={[
                                            (option: Option<IAdministratorOverview>, rowIndex: number, cellIndex: number) => {
                                                return null;
                                            },
                                            dataCell('email'),
                                            dataCell('status')
                                        ]}
                                        onRowClicked={(option: Option<IAdministratorOverview>, rowIndex: number) => {
                                            onAdminRowClicked(option.object)
                                            return;
                                        }}
                                    />
                                </Container>
                            </Paper>
                        </Grid>
                    </Grid>
                    {props.tableController.tableState.loading ? <FormLoadingSpinner /> : null}
                    {/* New Admin Dialog */}
                    <RecordEditorDialog
                        record={newRecord}
                        copyRecord={record => new OrganizationAdminInvite(record)}
                        state={dialogStates[DialogType.AddNew]}
                        title={'Add Administrator'}
                        onConfirm={onConfirmNewAdmin}
                        confirmText={'Add'}
                        DialogProps={{ maxWidth: 'xs' }}
                    >
                        {(record: OrganizationAdminInvite) =>
                            <Fragment>
                                {addRecordError &&
                                    <Alert severity="error" className={classes.alert}>
                                        <AlertTitle>Oops! There was an error with your submission.</AlertTitle>
                                        {addRecordError.message}
                                    </Alert>
                                }
                                <AddAdminForm newRecord={record} error={clientValidationError} />
                            </Fragment>
                        }
                    </RecordEditorDialog>
                    {/* Delete Administrator Confirmation */}
                    <ConfirmationDialog
                        state={dialogStates.delete}
                        title={`Delete ${loadedRecords.selections.length > 1 ? 'Admins' : 'Admin'}`}
                        content={
                            <Fragment>
                                {isDeletingAllRemainingAdmins()
                                    ? <Alert severity="error" className={classes.alert}>
                                        <AlertTitle>Deletion Not Permitted</AlertTitle>
                                        {"There must always be at least one active administrator remaining. Deselect one or more active administrators to proceed."}
                                    </Alert>
                                    : isDeletingSelf()
                                        ? <Alert severity="warning" className={classes.alert}>
                                            <AlertTitle>Forfeiting Administrator Access</AlertTitle>
                                            {loadedRecords.selections.length > 1 ? "You've included yourself in the set of administrators to delete. " : ""}
                                            {"If you proceed, you will lose administrator access immediately."}
                                        </Alert>
                                        : null
                                }
                                {isDeletingAllRemainingAdmins()
                                    ? null
                                    : <Typography>
                                        Are you sure you want to {loadedRecords.selections.length > 1
                                            ? 'delete these administrators'
                                            : isDeletingSelf()
                                                ? 'remove yourself as an administrator'
                                                : 'delete this administrator'}?
                                    </Typography>
                                }
                            </Fragment>
                        }
                        confirmText={isDeletingAllRemainingAdmins() ? 'Okay' : 'Delete'}
                        onConfirm={isDeletingAllRemainingAdmins() ? () => dialogStates.delete.setOpen(false) : onDeletionConfirmed}
                        fullScreen={false}
                    />
                </Container>
            )}
        </Editor>
    );
});

export default AdministratorsOverview;